import { useState } from 'react';
import { AlertTriangle, Download, FileDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '$lib/components/ui/Button';
import { Input } from '$lib/components/ui/Input';
import { Badge } from '$lib/components/ui/Badge';
import { Skeleton } from '$lib/components/ui/Skeleton';
import { buildListingsExportUrl } from '$lib/api/client';
import { fetchRentalAssessment, downloadRentalAssessmentPdf } from '$lib/api/reportApi';
import type { ReportParams, ReportResult, ReportComparable, PropertyType } from '$lib/api/reportApi';
import { formatKr, formatKrPerSqm, formatSqm, formatRooms, formatDate, formatNumber } from '$lib/lib/formatters';
import { cn } from '$lib/lib/cn';

export function ReportPage() {
  const { t } = useTranslation();
  const [postnummer, setPostnummer] = useState('');
  const [address, setAddress] = useState('');
  const [propertyType, setPropertyType] = useState<PropertyType | undefined>(undefined);
  const [areaMin, setAreaMin] = useState('');
  const [areaMax, setAreaMax] = useState('');
  const [roomsMin, setRoomsMin] = useState('');
  const [roomsMax, setRoomsMax] = useState('');
  const [result, setResult] = useState<ReportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canPreview = postnummer.trim().length > 0;

  const currentParams: ReportParams = {
    postnummer: postnummer.trim(),
    ...(address.trim() ? { address: address.trim() } : {}),
    ...(propertyType ? { propertyType } : {}),
    ...(areaMin ? { areaM2Min: Number(areaMin) } : {}),
    ...(areaMax ? { areaM2Max: Number(areaMax) } : {}),
    ...(roomsMin ? { roomsMin: Number(roomsMin) } : {}),
    ...(roomsMax ? { roomsMax: Number(roomsMax) } : {}),
  };

  async function handlePreview() {
    if (!canPreview) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchRentalAssessment(currentParams);
      setResult(data);
    } catch (e) {
      setError((e as Error).message ?? t('report.errorTitle'));
      setResult(null);
    } finally {
      setIsLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && canPreview) void handlePreview();
  }

  function handlePdfDownload() {
    downloadRentalAssessmentPdf(currentParams);
  }

  function handleExcelExport() {
    const url = buildListingsExportUrl({
      postnummer: postnummer.trim() ? [postnummer.trim()] : undefined,
      m2Min: areaMin ? Number(areaMin) : undefined,
      m2Max: areaMax ? Number(areaMax) : undefined,
      roomsMin: roomsMin ? Number(roomsMin) : undefined,
      roomsMax: roomsMax ? Number(roomsMax) : undefined,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col" data-testid="report-page">
      <header className="flex h-11 shrink-0 items-center gap-3 border-b border-rule bg-surface-2 px-3">
        <h1 className="text-sm font-semibold text-fg">{t('report.title')}</h1>
        {result && <Badge tone="info">{result.locationLabel}</Badge>}
      </header>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
        {/* Configurator sidebar */}
        <aside
          aria-label={t('report.configuratorAriaLabel')}
          className="shrink-0 overflow-y-auto border-b border-rule bg-surface p-4 md:w-64 md:border-b-0 md:border-r"
        >
          <div className="flex flex-col gap-5">
            {/* Location */}
            <section>
              <SectionLabel>{t('report.sectionLocation')}</SectionLabel>
              <div className="flex flex-col gap-2">
                <FieldRow label={t('report.postnummerLabel')} required>
                  <Input
                    value={postnummer}
                    onChange={(e) => setPostnummer(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t('report.postnummerPlaceholder')}
                    aria-label={t('report.postnummerLabel')}
                    data-testid="report-postnummer"
                  />
                </FieldRow>
                <FieldRow label={t('report.addressLabel')}>
                  <Input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t('report.addressPlaceholder')}
                    aria-label={t('report.addressLabel')}
                    data-testid="report-address"
                  />
                </FieldRow>
              </div>
            </section>

            {/* Property type */}
            <section>
              <SectionLabel>{t('report.sectionFilters')}</SectionLabel>
              <div className="flex flex-col gap-3">
                <FieldRow label={t('report.propertyTypeLabel')}>
                  <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('report.propertyTypeLabel')}>
                    {(['apartment', 'house', 'room'] as const).map((type) => (
                      <TypeChip
                        key={type}
                        active={propertyType === type}
                        onClick={() => setPropertyType(propertyType === type ? undefined : type)}
                      >
                        {t(`report.propertyType_${type}`)}
                      </TypeChip>
                    ))}
                  </div>
                </FieldRow>

                {/* Area range */}
                <FieldRow label={t('report.areaLabel')}>
                  <RangeInputs
                    minValue={areaMin}
                    maxValue={areaMax}
                    onMinChange={setAreaMin}
                    onMaxChange={setAreaMax}
                    onKeyDown={handleKeyDown}
                    minPlaceholder={t('common.min')}
                    maxPlaceholder={t('common.max')}
                    ariaLabel={t('report.areaLabel')}
                  />
                </FieldRow>

                {/* Rooms range */}
                <FieldRow label={t('report.roomsLabel')}>
                  <RangeInputs
                    minValue={roomsMin}
                    maxValue={roomsMax}
                    onMinChange={setRoomsMin}
                    onMaxChange={setRoomsMax}
                    onKeyDown={handleKeyDown}
                    minPlaceholder={t('common.min')}
                    maxPlaceholder={t('common.max')}
                    ariaLabel={t('report.roomsLabel')}
                  />
                </FieldRow>
              </div>
            </section>

            <Button
              variant="primary"
              onClick={() => void handlePreview()}
              disabled={!canPreview || isLoading}
              data-testid="report-preview-button"
              className="w-full"
            >
              <Search size={14} />
              {isLoading ? t('common.loading') : t('report.previewButton')}
            </Button>

            {!canPreview && (
              <p className="text-2xs text-fg-muted" role="note">
                {t('report.postnummerRequired')}
              </p>
            )}
          </div>
        </aside>

        {/* Preview panel */}
        <section
          aria-label={t('report.previewAriaLabel')}
          className="flex min-w-0 flex-1 flex-col overflow-y-auto p-4"
          data-testid="report-preview"
        >
          <PreviewContent
            result={result}
            isLoading={isLoading}
            error={error}
            canExport={canPreview}
            onPdfDownload={handlePdfDownload}
            onExcelExport={handleExcelExport}
          />
        </section>
      </div>
    </div>
  );
}

// --- Configurator sub-components ---

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2 text-2xs font-semibold uppercase tracking-wide text-fg-subtle">{children}</p>
  );
}

function FieldRow({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-2xs text-fg-muted">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </label>
      {children}
    </div>
  );
}

function TypeChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex h-7 items-center rounded-sm border px-2.5 text-xs transition-colors duration-fast',
        active
          ? 'border-accent/45 bg-accent/10 text-accent'
          : 'border-rule-strong bg-surface text-fg-muted hover:bg-surface-2 hover:text-fg'
      )}
    >
      {children}
    </button>
  );
}

function RangeInputs({
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  onKeyDown,
  minPlaceholder,
  maxPlaceholder,
  ariaLabel,
}: {
  minValue: string;
  maxValue: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  minPlaceholder: string;
  maxPlaceholder: string;
  ariaLabel: string;
}) {
  return (
    <div className="flex items-center gap-1.5" role="group" aria-label={ariaLabel}>
      <Input
        type="number"
        value={minValue}
        onChange={(e) => onMinChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={minPlaceholder}
        min={0}
        className="w-full"
        aria-label={`${ariaLabel} ${minPlaceholder}`}
      />
      <span className="shrink-0 text-xs text-fg-muted">–</span>
      <Input
        type="number"
        value={maxValue}
        onChange={(e) => onMaxChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={maxPlaceholder}
        min={0}
        className="w-full"
        aria-label={`${ariaLabel} ${maxPlaceholder}`}
      />
    </div>
  );
}

// --- Preview panel sub-components ---

function PreviewContent({
  result,
  isLoading,
  error,
  canExport,
  onPdfDownload,
  onExcelExport,
}: {
  result: ReportResult | null;
  isLoading: boolean;
  error: string | null;
  canExport: boolean;
  onPdfDownload: () => void;
  onExcelExport: () => void;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-sm" />
          ))}
        </div>
        <Skeleton className="h-48 rounded-sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="flex flex-col items-center gap-3 py-16 text-center">
        <AlertTriangle size={24} className="text-danger" />
        <p className="text-sm font-medium text-fg">{t('report.errorTitle')}</p>
        <p className="max-w-sm text-2xs text-fg-muted">{error}</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <Search size={24} className="text-fg-muted" />
        <p className="text-sm font-medium text-fg">{t('report.emptyTitle')}</p>
        <p className="max-w-xs text-2xs text-fg-muted">{t('report.emptyBody')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Summary stats */}
      <div>
        <h2 className="mb-2 text-xs font-semibold text-fg">{t('report.sectionSummary')}</h2>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          <StatCard label={t('report.statListingCount')} value={formatNumber(result.summary.listingCount)} />
          <StatCard label={t('report.statMedianRent')} value={formatKr(result.summary.medianRent)} />
          <StatCard label={t('report.statMedianKrPerSqm')} value={formatKrPerSqm(result.summary.medianKrPerSqm)} />
          <StatCard label={t('report.statP25Rent')} value={formatKr(result.summary.p25Rent)} tone="muted" />
          <StatCard label={t('report.statP75Rent')} value={formatKr(result.summary.p75Rent)} tone="muted" />
          <StatCard label={t('report.statP25KrPerSqm')} value={formatKrPerSqm(result.summary.p25KrPerSqm)} tone="muted" />
          <StatCard label={t('report.statP75KrPerSqm')} value={formatKrPerSqm(result.summary.p75KrPerSqm)} tone="muted" />
        </div>
      </div>

      {/* Export buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={onPdfDownload}
          disabled={!canExport}
          data-testid="report-download-pdf"
        >
          <FileDown size={13} />
          {t('report.exportPdf')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onExcelExport}
          disabled={!canExport}
          data-testid="report-export-excel"
        >
          <Download size={13} />
          {t('report.exportExcel')}
        </Button>
      </div>

      {/* Comparables table */}
      {result.comparables.length > 0 && (
        <div>
          <h2 className="mb-2 text-xs font-semibold text-fg">{t('report.sectionComparables')}</h2>
          <ComparablesTable rows={result.comparables} />
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = 'normal' }: { label: string; value: string; tone?: 'normal' | 'muted' }) {
  return (
    <div className="rounded-sm border border-rule bg-surface p-3">
      <p className="text-2xs text-fg-muted">{label}</p>
      <p className={cn('num mt-1 text-sm font-semibold', tone === 'muted' ? 'text-fg-muted' : 'text-fg')}>
        {value}
      </p>
    </div>
  );
}

function ComparablesTable({ rows }: { rows: ReportComparable[] }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-sm border border-rule">
      <table
        className="w-full min-w-[600px] text-xs"
        aria-label={t('report.sectionComparables')}
      >
        <thead className="border-b border-rule bg-surface-2">
          <tr>
            <Th>{t('report.columnAddress')}</Th>
            <Th align="right">{t('report.columnArea')}</Th>
            <Th align="right">{t('report.columnRooms')}</Th>
            <Th align="right">{t('report.columnRent')}</Th>
            <Th align="right">{t('report.columnKrPerSqm')}</Th>
            <Th>{t('report.columnSource')}</Th>
            <Th>{t('report.columnListed')}</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-rule last:border-0 hover:bg-surface-2 transition-colors duration-fast"
            >
              <td className="px-3 py-2 text-fg">
                {row.address}
                <span className="ml-1.5 text-fg-muted">{row.postnummer}</span>
              </td>
              <Td align="right">{formatSqm(row.m2)}</Td>
              <Td align="right">{formatRooms(row.rooms)}</Td>
              <Td align="right">{formatKr(row.rentKrPerMonth)}</Td>
              <Td align="right">{formatKrPerSqm(row.krPerSqm)}</Td>
              <Td>{row.source}</Td>
              <Td>{formatDate(row.listedAt)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={cn(
        'px-3 py-2 text-2xs font-semibold uppercase tracking-wide text-fg-subtle',
        align === 'right' ? 'text-right' : 'text-left'
      )}
    >
      {children}
    </th>
  );
}

function Td({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <td className={cn('num px-3 py-2 text-fg-muted', align === 'right' ? 'text-right' : 'text-left')}>
      {children}
    </td>
  );
}
